/**
 * <LiveReportsListPage> — the "/menu/reports" parent screen rebuilt on the DS (page, L0.1).
 *
 * Bottom-tab chrome is owned by the parent navigator. Pure composition — data in as
 * `UILiveSessionReport[]`, events out as callbacks. First-load / error via `<PageTemplate>`;
 * scroll + pagination via `<InfiniteList>` (`scroll={false}` on the shell) — la suite en route
 * s'annonce par deux cartes squelettes au pied de la liste.
 */
import React from "react"

import { InfiniteList } from "../../molecules/InfiniteList"
import { LiveReportCard } from "../../organisms/LiveReportCard"
import { LiveReportCardSkeleton } from "../../organisms/LiveReportCard/LiveReportCardSkeleton"
import { PageTemplate, PARENT_CONTENT_WIDTH } from "../../templates/PageTemplate"
import { type UILiveSessionReport } from "../../../ports/primary/ui/types"
import { LiveReportsListSkeleton } from "./LiveReportsListSkeleton"

export type LiveReportsListPageProps = {
    reports: UILiveSessionReport[]
    /** Opens a report's full detail ("Voir le rapport complet"). */
    onOpenReport?: (report: UILiveSessionReport) => void
    loading?: boolean
    refreshing?: boolean
    loadingMore?: boolean
    /** Next-page failure while rows are already shown — footer banner + retry. */
    loadMoreError?: string | null
    hasMore?: boolean
    /** API `meta.total` — footer shows `{loaded} / {total}`. */
    totalCount?: number
    onRefresh?: () => void
    onLoadMore?: () => void
    error?: unknown
    onRetry?: () => void
    /**
     * Au-dessus des rapports, et qui défile avec eux. Absent (défaut) : rien, l'onglet du parent n'en a
     * pas. Le tuteur y pose le diagnostic (`Pages/Tuteur/Rapports de l'élève`).
     */
    listHeader?: React.ReactElement
    testID?: string
}

export function LiveReportsListPage(props: LiveReportsListPageProps) {
    const {
        reports,
        onOpenReport,
        loading,
        refreshing,
        loadingMore,
        loadMoreError,
        hasMore,
        totalCount,
        onRefresh,
        onLoadMore,
        error,
        onRetry,
        listHeader,
        testID,
    } = props

    return (
        <PageTemplate
            scroll={false}
            loading={loading}
            skeleton={<LiveReportsListSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="0"
            maxContentWidth={PARENT_CONTENT_WIDTH}
            testID={testID}
        >
            <InfiniteList
                data={reports}
                header={listHeader}
                keyExtractor={(report) => String(report.id)}
                renderItem={(report) => <LiveReportCard report={report} onOpenReport={() => onOpenReport?.(report)} />}
                emptyMessage="Aucun rapport pour le moment"
                refreshing={refreshing}
                onRefresh={onRefresh}
                loadingMore={loadingMore}
                moreSkeleton={
                    <>
                        <LiveReportCardSkeleton commentLines={2} />
                        <LiveReportCardSkeleton commentLines={2} />
                    </>
                }
                loadMoreError={loadMoreError}
                onRetryLoadMore={onLoadMore}
                hasMore={hasMore}
                totalCount={totalCount}
                onEndReached={onLoadMore}
            />
        </PageTemplate>
    )
}
